import { useCallback, useMemo, useState } from 'react';

import { VISIBLE_LIMIT } from '../constants';
import type { ChildrenPaginationInfo, ChildrenSnapshot, RichTreeItem } from '../types';

/**
 * Owns everything related to "opened" parents and their children pagination.
 *
 * Data flow:
 *  1. `openParent(treeId, dataNodeId)` is called when the user expands a
 *     node — the parent is added to `openedParents`.
 *  2. The owning component renders one `<ChildrenSubscriber />` per opened
 *     parent. Each subscriber wraps a `useInfiniteQuery` and emits
 *     snapshots into `handleSnapshot`.
 *  3. `handleSnapshot` denormalises snapshots into `loadedChildren`,
 *     `childrenPagination` and `loadingParentIds` — flat maps that the
 *     tree-derivation utilities and `<CustomTreeItem />` consume.
 *  4. `handleShowMore` flips `expandedParents` and/or invokes the
 *     subscriber's `fetchNextPage` to load more items.
 *
 * Because react-query owns the cache, SignalR-driven invalidations
 * (`treeQueryKeys.children(...)`) flow back into subscribers automatically:
 * they re-emit snapshots and the tree re-renders. No extra plumbing.
 *
 * Collapsing a node unmounts its subscriber and drops its snapshot — a full
 * unsubscribe. The react-query cache retains the data (kept fresh by SignalR),
 * so re-expanding re-emits from cache without a refetch. `closeAllParents`
 * clears everything.
 */
export function useChildrenCache() {
  const [openedParents, setOpenedParents] = useState<Map<string, string>>(new Map());
  const [snapshots, setSnapshots] = useState<Map<string, ChildrenSnapshot>>(new Map());
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  const openParent = useCallback((parentTreeId: string, parentDataNodeId: string) => {
    setOpenedParents((prev) => {
      if (prev.get(parentTreeId) === parentDataNodeId) return prev;
      const next = new Map(prev);
      next.set(parentTreeId, parentDataNodeId);
      return next;
    });
  }, []);

  /**
   * Inverse of `openParent`. Unmounts the matching `<ChildrenSubscriber />`,
   * clears the local "Show more" flag and drops the children snapshot entirely —
   * collapsing is a full unsubscribe. The data stays in the react-query cache
   * (kept fresh by SignalR while collapsed), so re-expanding remounts the
   * subscriber and re-emits synchronously from cache without a refetch. Keeping
   * the snapshot instead would leave it stale: with no subscriber feeding it, a
   * node whose children were all moved away elsewhere would keep showing a stale
   * expand chevron.
   */
  const closeParent = useCallback((parentTreeId: string) => {
    setOpenedParents((prev) => {
      if (!prev.has(parentTreeId)) return prev;
      const next = new Map(prev);
      next.delete(parentTreeId);
      return next;
    });
    setExpandedParents((prev) => {
      if (!prev.has(parentTreeId)) return prev;
      const next = new Set(prev);
      next.delete(parentTreeId);
      return next;
    });
    setSnapshots((prev) => {
      if (!prev.has(parentTreeId)) return prev;
      const next = new Map(prev);
      next.delete(parentTreeId);
      return next;
    });
  }, []);

  const closeAllParents = useCallback(() => {
    setOpenedParents((prev) => (prev.size === 0 ? prev : new Map()));
    setSnapshots((prev) => (prev.size === 0 ? prev : new Map()));
    setExpandedParents((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const handleSnapshot = useCallback((parentTreeId: string, snapshot: ChildrenSnapshot) => {
    setSnapshots((prev) => {
      // Skip the update if the snapshot is materially equivalent to the one
      // we already hold. ChildrenSubscriber re-emits on every render whose
      // useInfiniteQuery flips `isFetchingNextPage` (twice per page), so
      // without this guard we'd clone the whole Map on every transient flip
      // and propagate identity changes through every consumer.
      const existing = prev.get(parentTreeId);
      if (
        existing &&
        existing.items === snapshot.items &&
        existing.hasMore === snapshot.hasMore &&
        existing.isFetching === snapshot.isFetching &&
        existing.isError === snapshot.isError &&
        existing.fetchNextPage === snapshot.fetchNextPage
      ) {
        return prev;
      }
      const next = new Map(prev);
      next.set(parentTreeId, snapshot);
      return next;
    });
  }, []);

  /**
   * "Show more" semantics:
   *  - If the parent is collapsed and there are locally loaded items beyond
   *    `VISIBLE_LIMIT`, simply expand to reveal them.
   *  - Otherwise, if the backend has more pages, fetch the next page and
   *    ensure the parent is expanded so the newly fetched items become
   *    visible at once.
   */
  const handleShowMore = useCallback(
    (parentTreeId: string) => {
      const snap = snapshots.get(parentTreeId);
      if (!snap || snap.isFetching) return;

      const isExpanded = expandedParents.has(parentTreeId);
      const canExpandLocally = !isExpanded && snap.items.length > VISIBLE_LIMIT;

      if (canExpandLocally) {
        setExpandedParents((prev) => {
          const next = new Set(prev);
          next.add(parentTreeId);
          return next;
        });
        return;
      }

      if (snap.hasMore) {
        if (!isExpanded) {
          setExpandedParents((prev) => {
            const next = new Set(prev);
            next.add(parentTreeId);
            return next;
          });
        }
        snap.fetchNextPage();
      }
    },
    [snapshots, expandedParents]
  );

  const { loadedChildren, childrenPagination, loadingParentIds } = useMemo(() => {
    const loaded: Record<string, RichTreeItem[]> = {};
    const pagination: Record<string, ChildrenPaginationInfo> = {};
    const loading = new Set<string>();
    snapshots.forEach((snap, parentTreeId) => {
      loaded[parentTreeId] = snap.items;
      pagination[parentTreeId] = { hasMore: snap.hasMore };
      if (snap.isFetching) loading.add(parentTreeId);
    });

    return { loadedChildren: loaded, childrenPagination: pagination, loadingParentIds: loading };
  }, [snapshots]);

  return {
    openedParents,
    openParent,
    closeParent,
    closeAllParents,
    handleSnapshot,
    handleShowMore,
    loadedChildren,
    childrenPagination,
    loadingParentIds,
    expandedParents
  };
}
